import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { connect } from "react-redux";
import { useNavigate, useParams, useBlocker } from "react-router-dom";
import Select from "react-select";
import CheckBox from "../../../components/CheckBoxes/CheckBox";
import EmailField from "../../../components/Email/EmailField";
import HeaderBar from "../../../components/Header/HeaderBar";
import ImageNoteScroller from "../../../components/Images/ImageNoteScroller";
import LabeledTextboxInput from "../../../components/Inputs/LabeledTextboxInput";
import ReportSettingsField from "../../../components/Report/ReportSettingsField";
import ServicesField from "../../../components/Services/ServicesField";
import SnowIcon from "../../../resources/logo/snow-logo-blue.svg";
import LandscapeIcon from "../../../resources/logo/landscape-logo-dark.svg";
import { JobServiceOption } from "../../../services/axios/jobs";
import { defaultSeasonValues } from "./job-hooks";
import { EditMapLink } from "../../../components/SiteMap/EditMapLink";
import axios, { isAxiosError } from "../../../services/axios/backendAxios";
import flash from "../../../services/flash";
import errors from "../../../services/errors";
import { CurrentUser } from "../../../store/storeTypes";
import ContentWrapper from "../../../components/ContentWrapper";
import PlaceAutocomplete from "./PlaceAutocomplete";
import { API_KEY } from "../../../config/maps";
import { APIProvider } from "@vis.gl/react-google-maps";
import { Company, Season } from "../../../types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { pickSeason } from "../../../components/Modals/PickSeason";
import { formatAddress } from "../Routes/RoutePage/Routes/utils";
import LoadingIcon from "../../../components/Loaders/LoadingIcon";
import AuthLabeledTextInput from "../../../components/Inputs/AuthLabeledTextInput";
import SelectOnMap from "../../../components/SelectOnMap";
import { slug } from "../../../utils/slug";
import {
  auth,
  Job as BaseJobInterface,
  SeasonDetails,
  relativeShareInfo,
  RelativeShareInfo,
  EmailReportSettings,
} from "@mysnowscape/snow-lib";
import { format } from "date-fns";
import { dateFromMongoId } from "../../../utils/mongo";
import BasicInput from "../../../components/Inputs/BasicInput";
import BlockerDialog from "../../../components/Modals/BlockerDialog";
import useDirtyState from "../../../hooks/useDirtyState";
import { formatErrorMessage } from "../../../services/axios/APIError";
import SubtleButton from "../../../components/Buttons/SubtleButton";
import { useFeatureFlag, User } from "configcat-react";

interface JobInterface extends BaseJobInterface {
  __v: any;
}

function buildNewJobWithDefault(company: Company): Partial<JobInterface> {
  return {
    companyId: company._id,
    landscape: {
      ...defaultSeasonValues(),
      serviceOptions: company.commonCompanyServices.landscape,
      enabled: false,
    },
    snow: {
      ...defaultSeasonValues(),
      serviceOptions: company.commonCompanyServices.snow,
      enabled: false,
    },
  };
}
export interface CurrentUserMaybeId extends CurrentUser {
  _id?: string;
}
const EditJobPage = ({
  currentUser,
}: {
  currentUser: CurrentUserMaybeId;
  displayMapThumbnail: boolean;
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { jobId } = useParams();
  const isNewJob = jobId === "create";
  const jobState = useDirtyState<Partial<JobInterface> | null>(null);
  const job = jobState.value;
  const setJobAndMarkDirty = jobState.modifyValue;
  const isDirty = jobState.isDirty;
  const [showChoosePoint, setShowChoosePoint] = useState(false);
  let { value: showSharePropertyButtons } = useFeatureFlag(
    "showSharePropertyButtons",
    false,
    currentUser._id
      ? new User(currentUser._id, undefined, undefined, {
          companyId: currentUser.currentCompanyId ?? "unknown",
        })
      : undefined,
  );
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (
      jobState.isDirtyRef.current &&
      currentLocation.pathname !== nextLocation.pathname &&
      !nextLocation.pathname.startsWith(`/admin/property/${jobId}/site-map`) &&
      !/^\/admin\/property\/[^/]+$/.test(nextLocation.pathname)
    ) {
      return true;
    }
    return false;
  });
  const companyQuery = useQuery<Company>({
    queryKey: [`company/${currentUser?.currentCompanyId}`],
    enabled: currentUser?.currentCompanyId != null,
  });
  const jobQuery = useQuery<JobInterface>({
    queryKey: [`job/${jobId}`],
    enabled:
      !isNewJob &&
      typeof jobId === "string" &&
      jobId.length > 0 &&
      currentUser?.currentCompanyId != null,
  });
  const createJobMutation = useMutation({
    mutationFn: async (job: Partial<JobInterface>) => {
      const newJob = (
        await axios.post(
          `job/${isNewJob ? "add" : "update"}?currentCompanyId=${currentUser.currentCompanyId}`,
          job,
        )
      ).data.job;
      return newJob;
    },
    onSuccess: async (newJob) => {
      flash.success("Property Saved");
      await queryClient.invalidateQueries({
        queryKey: ["jobs"],
        refetchType: "all", // TODO: not sure why we need this set to 'all', but without it the properties list doesn't seem to refetch.
      });
      jobState.clean(newJob);
      if (isNewJob) {
        navigate(`/admin/property/${newJob._id}`);
      }
    },
    onError: (err) => {
      errors.report(err);
      flash.error(formatErrorMessage(err));
    },
  });
  const deleteJobMutation = useMutation({
    mutationFn: async (jobId: string) => {
      await axios.delete("job/" + jobId + "/delete");
    },
    onSuccess: () => {
      flash.success("Property Deleted");
      navigate(`/admin/property`);
    },
    onError: (err) => {
      errors.report(err);
      flash.error(formatErrorMessage(err));
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["jobs"],
        refetchType: "all", // TODO: not sure why we need this set to 'all', but without it the properties list doesn't seem to refetch.
      });
    },
  });
  const activateJobMutation = useMutation({
    mutationFn: async ({
      jobId,
      newActiveValue,
    }: {
      jobId: string;
      newActiveValue: boolean;
    }) => {
      const response = await axios.post(
        "job/" + jobId + "/" + (newActiveValue ? "activate" : "inactivate"),
      );
      return response.data as {
        success: boolean;
        job?: JobInterface;
        removedFromRouteCount?: number;
      };
    },
    onSuccess: (result, variables) => {
      if (variables.newActiveValue) {
        flash.success("Property activated");
      } else {
        const count = result.removedFromRouteCount ?? 0;
        flash.success(
          count > 0
            ? `Property inactivated and removed from ${count} route${count === 1 ? "" : "s"}.`
            : "Property inactivated",
        );
      }
      if (result.job) {
        // Keep the single-property query cache in sync. Without this, navigating
        // away and back re-seeds jobState from stale cached data and the
        // activate/inactivate state (and banner) can be wrong until a refresh.
        queryClient.setQueryData([`job/${variables.jobId}`], result.job);
        jobState.modifyValue((old) => ({ ...old, ...result.job }), false);
      }
    },
    onError: (err) => {
      errors.report(err);
      flash.error(formatErrorMessage(err));
    },
    onSettled: async (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: [`job/${variables.jobId}`],
      });
      await queryClient.invalidateQueries({
        queryKey: ["jobs"],
        refetchType: "all", // TODO: not sure why we need this set to 'all', but without it the properties list doesn't seem to refetch.
      });
    },
  });
  const removeShareJobMutation = useMutation({
    mutationFn: async (companyIds: string[]) => {
      let tasks = [];
      for (const cid of companyIds) {
        tasks.push(axios.delete(`job/${job!._id}/share/${cid}`));
      }
      await Promise.all(tasks);
    },
    onSuccess: () => {
      flash.success(
        "Sharing Stopped. They will still have access to historic service records they submitted.",
      );
      queryClient.invalidateQueries({
        queryKey: ["jobs"],
      });
      jobState.modifyValue((old) => {
        return { ...old, sharedWith: [] };
      }, false); // Here we can safely skip the dirty flag since the change is already saved.
    },
    onError: (err) => {
      errors.report(err);
      flash.error(formatErrorMessage(err));
    },
    retry: 3,
  });
  // Set our job state to null when we unmount (to help avoid issues with stale data)
  useEffect(() => {
    return () => {
      if (job !== null) {
        console.log("setting job state to null!!!!!!!!!!!!!!!!!!!!!!!!!");
        jobState.clean(null);
      }
    };
  }, []);
  const queryRunning =
    jobQuery.isLoading ||
    createJobMutation.isPending ||
    activateJobMutation.isPending ||
    deleteJobMutation.isPending;

  let validationInfo = null;
  if (!isNewJob && job?._id) {
    validationInfo = jobValidationStatus(job);
  }
  // TODO: we can use the following messages to display validation info near the fields that they apply to.
  // const validationMessages = validationInfo?.messages ?? [];

  // If we have an existing job, set up the data once we have it:
  useEffect(() => {
    if (!isNewJob && jobQuery.isSuccess && companyQuery.isSuccess) {
      if (job === null) {
        // We have a new job and we just loaded data for the first time.
        const myJob = jobQuery.data;
        if ((myJob.landscape?.serviceOptions?.length ?? 0) < 1) {
          if (myJob.landscape == null) {
            myJob.landscape = { enabled: false };
          }
          myJob.landscape.serviceOptions =
            companyQuery.data?.commonCompanyServices.landscape;
        }
        if ((myJob.snow?.serviceOptions?.length ?? 0) < 1) {
          if (myJob.snow == null) {
            myJob.snow = { enabled: false };
          }
          myJob.snow.serviceOptions =
            companyQuery.data?.commonCompanyServices.snow;
        }
        jobState.clean(myJob);
      } else {
        // Check if the new data is a new version:
        if (typeof job.__v === "number" && jobQuery.data.__v > job.__v) {
          // Backend has new data
          if (jobState.isDirty) {
            // Alert user
            alert(
              "WARNING: this job has been modified since you loaded the page. Please refresh the page now to load the new data.",
            );
          } else {
            console.log("Swapping out job data with newer data");
            jobState.clean(jobQuery.data); // we can swap out the data since the user hasn't touched it.
          }
        }
      }
    }
  }, [
    jobQuery.isSuccess,
    job,
    jobId,
    jobQuery.data,
    companyQuery.isSuccess,
    companyQuery.data?.commonCompanyServices.landscape,
    companyQuery.data?.commonCompanyServices.snow,
    isNewJob,
    jobState,
  ]);

  // If we have a new job, setup the blank info as soon as we have a company:
  useEffect(() => {
    if (isNewJob && job === null && companyQuery.isSuccess) {
      setJobAndMarkDirty(buildNewJobWithDefault(companyQuery.data));
    }
  }, [
    companyQuery.data,
    companyQuery.isSuccess,
    isNewJob,
    job,
    jobId,
    setJobAndMarkDirty,
  ]);

  if (jobQuery.isError) {
    return (
      <ErrorMessage
        error={jobQuery.error}
        defaultMessage="Unable to load property information"
      />
    );
  }
  if (companyQuery.isError) {
    return (
      <ErrorMessage
        error={companyQuery.error}
        defaultMessage="Unable to load your company information"
      />
    );
  }
  if (job === null) {
    return <LoadingIcon />;
  } else {
    let shareInfo;
    try {
      shareInfo = relativeShareInfo(
        job as JobInterface,
        currentUser.currentCompanyId!,
        job.companyName!,
      );
    } catch (err) {
      errors.report(err);
      shareInfo = {
        description: "This is a shared property",
        youShared: false,
        sharedWithYou: !(
          job.companyId?.toString() === currentUser.currentCompanyId!.toString()
        ),
        youSharedWithIds: [],
        amOwner:
          job.companyId?.toString() ===
          currentUser.currentCompanyId!.toString(),
      };
    }
    const hasPlaceInfo =
      job.location?.lat &&
      (job.address?.city ||
        job.address?.state ||
        job.address?.streetLineOne ||
        job.address?.zip);
    return (
      <div className="flex flex-col items-center">
        <HeaderBar
          title={job?.name ?? "Create Property"}
          buttons={[
            ...(showSharePropertyButtons
              ? [
                  {
                    color: "blue",
                    label: "Merge",
                    disabled: shareInfo.youShared || shareInfo.sharedWithYou,
                    loading: false,
                    onClick: async () => {
                      navigate(`/admin/property/${job._id}/merge`);
                    },
                  },
                  {
                    color: "blue",
                    label: "Share",
                    disabled:
                      job._id == null ||
                      isDirty ||
                      queryRunning ||
                      !shareInfo.amOwner,
                    loading: false,
                    onClick: async () => {
                      navigate(`/admin/property/share?jobIds=${job._id}`);
                    },
                  },
                ]
              : []),
            {
              color: "blue",
              label: "Site Map",
              disabled:
                job.address?.city == null || queryRunning || !shareInfo.amOwner,
              loading: false,
              onClick: async () => {
                const newJob = await createJobMutation.mutateAsync(job);
                const season = await pickSeason();
                navigate(
                  `/admin/property/${newJob._id}/site-map/${season}/edit`,
                );
              },
            },
            {
              color: "red",
              label: "Delete",
              disabled: isNewJob || queryRunning || !shareInfo.amOwner,
              loading: deleteJobMutation.isPending,
              onClick: async () => {
                await deleteJobMutation.mutateAsync(jobId!);
              },
            },
            {
              color: "orange",
              label:
                jobState.value?.inactivatedAt == null
                  ? "Inactivate"
                  : "Activate",
              disabled:
                isNewJob ||
                queryRunning ||
                !job.companyId ||
                !auth.canAdminCompany(currentUser, job.companyId.toString()),
              loading: activateJobMutation.isPending,
              onClick: async () => {
                const currentlyActive = jobState.value?.inactivatedAt == null;
                if (
                  !currentlyActive ||
                  confirm(
                    "Inactivating this property will remove it from routes. Continue?",
                  )
                ) {
                  await activateJobMutation.mutateAsync({
                    jobId: jobId!,
                    newActiveValue: !currentlyActive,
                  });
                }
              },
            },
            {
              color: "green",
              label: `${isDirty ? "*" : ""}Save`,
              disabled: !hasPlaceInfo || queryRunning,
              loading: createJobMutation.isPending,
              onClick: () => {
                createJobMutation.mutate(job);
              },
            },
          ]}
        />
        <BlockerDialog blocker={blocker} />
        <ContentWrapper loading={false} className="">
          {job.inactivatedAt != null && (
            <div className="bg-orange text-white rounded p-4 mb-2 flex items-center justify-between gap-4">
              <span>
                This property was inactivated on{" "}
                {format(new Date(job.inactivatedAt), "MMM d yyyy")}.
              </span>
              <button
                type="button"
                className="underline font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={
                  isNewJob ||
                  queryRunning ||
                  !job.companyId ||
                  !auth.canAdminCompany(currentUser, job.companyId.toString())
                }
                onClick={() => {
                  activateJobMutation.mutate({
                    jobId: jobId!,
                    newActiveValue: true,
                  });
                }}
              >
                {activateJobMutation.isPending
                  ? "Re-Activating..."
                  : "Re-Activate"}
              </button>
            </div>
          )}
          {validationInfo !== null && validationInfo.messages.length > 0 && (
            <div className="bg-red-400 text-white rounded p-4 mb-2">
              {/* <div className="text-red-500 border-red-500 border"> */}
              <h3 className="font-bold">Property Data Incomplete</h3>
              <p>
                You will need to fill in the following information before adding
                this property to a route. However, you can still use site maps
                to create estimates for this property.
              </p>
              <ul className="list-disc ml-6">
                {validationInfo.messages.map((m, idx) => (
                  <li key={idx}>{m.message}</li>
                ))}
              </ul>
            </div>
          )}
          {job.sharedWith && job.sharedWith?.length > 0 && (
            <div className="bg-snow-white border rounded p-2 mb-2 flex justify-between">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-gray-600"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.02-4.11c.54.5 1.25.81 2.07.81 1.66 0 3-1.34 3-3S19.66 2 18 2s-3 1.34-3 3c0 .24.04.47.09.7L8.07 9.81C7.53 9.31 6.82 9 6 9 4.34 9 3 10.34 3 12s1.34 3 3 3c.82 0 1.53-.31 2.07-.81l7.12 4.17c-.05.21-.07.43-.07.64 0 1.66 1.34 3 3 3s3-1.34 3-3-1.34-3-3-3z"
                    fill="currentColor"
                  />
                </svg>
                {shareInfo.description}
              </div>
              {shareInfo.youShared && (
                <SubtleButton
                  type="button"
                  onClick={async () => {
                    await removeShareJobMutation.mutateAsync(
                      shareInfo.youSharedWithIds,
                    );
                  }}
                >
                  Stop Sharing
                </SubtleButton>
              )}
            </div>
          )}
          <APIProvider apiKey={API_KEY}>
            <div
              className="items-start gap-3"
              style={{ display: "grid", gridTemplateColumns: "auto 1fr" }}
            >
              {window.innerWidth > 600 && <MapThumb job={job} />}
              <div>
                <label className={`text-sm block`}>
                  Property Address{" "}
                  <span
                    className={`underline font-light text-gray-500 text-xs ${shareInfo.amOwner ? "cursor-pointer" : "opacity-50 cursor-not-allowed"}`}
                    onClick={
                      shareInfo.amOwner
                        ? (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowChoosePoint(true);
                          }
                        : undefined
                    }
                  >
                    {hasPlaceInfo ? "edit address" : "or select on map"}
                  </span>
                  {/* The place autocomplete costs to render so we only render it if we are editing or creating a new address. */}
                  {hasPlaceInfo ? (
                    <BasicInput
                      value={formatAddress(job.address)}
                      onClick={
                        shareInfo.amOwner
                          ? () => setShowChoosePoint(true)
                          : undefined
                      }
                      readOnly={true}
                      disabled={!shareInfo.amOwner}
                      name={undefined}
                      label={undefined}
                      type={undefined}
                      step={undefined}
                      maxLength={undefined}
                      minLength={undefined}
                      onChange={undefined}
                      placeholder={undefined}
                      pattern={undefined}
                      required={undefined}
                      className={undefined}
                      onInput={undefined}
                      testId={undefined}
                      defaultValue={undefined}
                      autoFocus={undefined}
                    />
                  ) : (
                    <div
                      className={
                        !shareInfo.amOwner
                          ? "pointer-events-none opacity-50"
                          : undefined
                      }
                    >
                      <PlaceAutocomplete
                        value={
                          job.address ? formatAddress(job.address) : undefined
                        }
                        onPlaceSelect={(place, address, location) => {
                          setJobAndMarkDirty((j) => ({
                            ...j,
                            address,
                            location: location ?? undefined,
                            name: j?.name ? j.name : address.name,
                          }));
                        }}
                      />
                    </div>
                  )}
                </label>
                <p className="text-xs text-gray-500 font-light">
                  {job.location == null
                    ? "Type (or paste) an address into the text input above and then select one of the matching options."
                    : `Lat/Lng: ${job.location.lat}, ${job.location.lng}`}
                </p>
                {job.location || job.name ? (
                  <AuthLabeledTextInput
                    value={job.name}
                    label="Property Name"
                    setValue={(v: string) =>
                      setJobAndMarkDirty((j) => ({ ...j, name: v }))
                    }
                    numbersOnly={undefined}
                    maxLength={undefined}
                    minLength={undefined}
                    onInput={undefined}
                    name={undefined}
                    testId={undefined}
                    className={undefined}
                    inputClassName={undefined}
                    inputMode={undefined}
                    placeholder={undefined}
                    pattern={undefined}
                    required={undefined}
                    defaultValue={undefined}
                    autoFocus={undefined}
                    showValidation={undefined}
                    disabled={!shareInfo.amOwner}
                  />
                ) : null}
              </div>
            </div>
            {showChoosePoint && (
              <SelectOnMap
                cancel={() => setShowChoosePoint(false)}
                onSelect={(result) => {
                  setJobAndMarkDirty((j) => ({
                    ...j,
                    address: result.address ?? j?.address,
                    location: result.location,
                    name: j?.name ? j.name : result.address?.name,
                  }));
                  setShowChoosePoint(false);
                }}
                currentLocation={job?.location}
                currentAddress={job?.address}
              />
            )}
          </APIProvider>
          {hasPlaceInfo ? (
            <div
              className={`mt-6 grid lg:grid-cols-[minmax(0,_1fr)_1px_minmax(0,_1fr)] gap-[40px]`}
            >
              <SeasonOptions
                season={Season.snow}
                job={job}
                saveJob={async () => {
                  return await createJobMutation.mutateAsync(job);
                }}
                setJob={setJobAndMarkDirty}
                isNewJob={isNewJob}
                currentUser={currentUser}
                shareInfo={shareInfo}
                company={companyQuery.data}
                // className="border-r pr-5"
              />
              <div className="bg-gray-200"></div>
              <SeasonOptions
                season={Season.landscape}
                job={job}
                saveJob={async () => {
                  return await createJobMutation.mutateAsync(job);
                }}
                setJob={setJobAndMarkDirty}
                isNewJob={isNewJob}
                currentUser={currentUser}
                shareInfo={shareInfo}
                company={companyQuery.data}
              />
            </div>
          ) : (
            <div className="text-gray-500 text-center mt-10">
              Please complete the address above before continuing.
            </div>
          )}
          {!isNewJob && job._id && (
            <div className="text-center py-2 text-gray-500 text-xs">
              Created {format(dateFromMongoId(job._id), "MMM d yyyy")}. v:
              {job?.__v}
            </div>
          )}
        </ContentWrapper>
      </div>
    );
  }
};

function SeasonOptions({
  job,
  setJob,
  season,
  saveJob,
  className,
  isNewJob,
  currentUser,
  shareInfo,
  company,
}: {
  job: Partial<JobInterface>;
  setJob: Dispatch<SetStateAction<Partial<JobInterface> | null>>;
  season: Season;
  saveJob: () => Promise<JobInterface>;
  className?: string;
  isNewJob: boolean;
  currentUser: CurrentUser;
  shareInfo: RelativeShareInfo;
  company?: Company;
}) {
  const amOwner = shareInfo.amOwner;
  const data: SeasonDetails = job[season] ?? ({} as SeasonDetails);
  const setSeasonField = useCallback(
    (field: keyof SeasonDetails, value: unknown) => {
      // setJob(j => ({ ...j, [field]: value }));
      setJob((j) => {
        const newValue = { ...j };
        const newSeason = newValue[season] ?? ({} as SeasonDetails);
        // @ts-expect-error not sure how to fix this ts error
        newSeason[field] = value;
        newValue[season] = newSeason;
        return newValue;
      });
    },
    [season, setJob],
  );
  const backupSiteMap = useCallback(() => {
    const geojson = data.siteMapGeoJson;
    if (!geojson) {
      flash.error("No site map data to backup");
      return;
    }
    const content =
      typeof geojson === "string" ? geojson : JSON.stringify(geojson, null, 2);
    const blob = new Blob([content], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const filename = `${slug(job.name ?? "property")}-${season}-sitemap.json`;
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [data.siteMapGeoJson, job, season]);

  const restoreSiteMap = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json,.json";
    input.onchange = async (e: Event) => {
      const target = e.target as HTMLInputElement | null;
      const file = target?.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const parsed = JSON.parse(text);
        setSeasonField("siteMapGeoJson", parsed);
        flash.success("Site map restored - don't forget to save!");
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        flash.error("Unable to read file: " + msg);
      } finally {
        // reset so the same file can be selected again
        input.value = "";
      }
    };
    input.click();
  }, [setSeasonField]);
  return (
    <div className={`overflow-hidden ${className ?? ""}`}>
      <h3
        className="text-center text-xl font-bold mb-5"
        style={{
          display: "grid",
          gridTemplateColumns: "20px auto 1fr",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <img
          className="w-5"
          src={season === "snow" ? SnowIcon : LandscapeIcon}
          alt=""
        />
        <span>{capitalizeFirstLetter(season)} Season</span>
        <CheckBox
          checked={data.enabled}
          lightMode={false}
          onClick={
            amOwner
              ? () => {
                  setSeasonField("enabled", !data.enabled);
                }
              : undefined
          }
          label="Enable"
          className={`my-0 place-self-end${!amOwner ? " opacity-50 pointer-events-none" : ""}`}
          testId={`enable-season-${season}`}
        />
      </h3>
      {data.enabled ? (
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-[10px] overflow-x-scroll">
            <div>
              <Label>
                Primary Site Map
                {job.companyId &&
                auth.can(currentUser, "restore-site-map", job.companyId) ? (
                  <span className="text-xs font-light text-gray-500 ml-2 inline-flex gap-2">
                    {data.siteMapGeoJson && (
                      <a
                        className="underline cursor-pointer"
                        onClick={(e) => {
                          e.preventDefault();
                          backupSiteMap();
                        }}
                      >
                        Backup
                      </a>
                    )}
                    <a
                      className="underline cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        restoreSiteMap();
                      }}
                    >
                      Restore
                    </a>
                  </span>
                ) : null}
              </Label>
              <EditMapLink
                beforeNavigate={
                  amOwner
                    ? async () => {
                        const newJob = await saveJob();
                        return {
                          continue: true,
                          propertyId: newJob._id.toString(),
                        };
                      }
                    : undefined
                }
                season={season}
                returnOnSave={isNewJob}
                startInEditMode={isNewJob}
                currentThumbnailUrl={job[season]?.primarySitemap}
                waitingForRender={job[season]?.sitemapNeedsNewImage === true}
                propertyId={job?._id?.toString() ?? undefined}
                disabled={!amOwner}
              />
            </div>
            <div>
              <Label>Additional Site Images / Maps</Label>
              <ImageNoteScroller
                enableDelete={amOwner}
                enableUpload={amOwner}
                isSitemap={true}
                imageArray={data.sitemaps || []}
                label=""
                setImageArray={(newArray: unknown) => {
                  setSeasonField("sitemaps", newArray);
                }}
                uploadBoxLabel="Drop or Click To Select"
              />
            </div>
          </div>

          <LabeledTextboxInput
            label="Crew Instructions"
            placeholder="Give special instructions to this property..."
            description="Property instructions visible to all staff and companies who have access to this property"
            className="bg-white"
            multiline={true}
            disabled={!amOwner}
            setValue={(val: string) => {
              setSeasonField("instructions", val);
            }}
            value={data.instructions}
          />
          {(shareInfo.youShared || shareInfo.sharedWithYou) && (
            <LabeledTextboxInput
              label="Private Instructions"
              description={`Give instructions to ${company?.name ?? "your company"} employees only. Will NOT be shared with other companies who have access to this property.`}
              className="bg-white"
              multiline={true}
              setValue={(val: string) => {
                setSeasonField("privateNotes", {
                  [company!._id.toString()]: val,
                });
              }}
              value={
                data.privateNotes && company
                  ? data.privateNotes[company._id.toString()]
                  : undefined
              }
            />
          )}

          <div className="flex flex-col w-full justify-start my-8">
            <div className="text-sm text-snow-primary font-bold mb-0.5">
              Property Images
            </div>
            <div className="text-sm text-snow-primary font-light mb-1">
              Select a quantity of images you want required to be taken by
              operators for this property. If you select "0" then operators will
              still have the option to upload images if they want to.
            </div>
            <RequiredImageSelector
              setSeasonField={setSeasonField}
              data={data}
              disabled={!amOwner}
            />
          </div>

          <div className="w-full mb-5">
            <div className="text-sm font-bold mb-0.5 self-start text-snow-primary">
              Notes
            </div>
            <div className="text-sm font-light mb-1 self-start text-snow-primary"></div>
            <CheckBox
              checked={data.requireNotes ?? true}
              label="Require operators to enter notes when working on this property"
              lightMode={false}
              onClick={
                amOwner
                  ? () => {
                      setSeasonField("requireNotes", !data.requireNotes);
                    }
                  : undefined
              }
              className={
                !amOwner ? "opacity-50 pointer-events-none" : undefined
              }
            />
          </div>

          <ServicesField
            enableAddNew={amOwner}
            enableSelectAll={amOwner}
            readOnly={!amOwner}
            label="Property Services"
            reorderable={true}
            services={data.serviceOptions ?? []}
            season={season}
            setServices={(updatedList: JobServiceOption[]) => {
              setSeasonField("serviceOptions", updatedList);
            }}
            subtitle={`Select what ${season} services you will provide to this property`}
          />
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr auto" }}
            className="mt-3"
          >
            <Label>Email Reports</Label>
            <div>
              <CheckBox
                checked={data.requireReportEmails ?? true}
                lightMode={false}
                onClick={
                  amOwner
                    ? () => {
                        setSeasonField(
                          "requireReportEmails",
                          !data.requireReportEmails,
                        );
                      }
                    : undefined
                }
                label="Enable"
                className={`my-0${!amOwner ? " opacity-50 pointer-events-none" : ""}`}
              />
            </div>
          </div>
          <EmailField
            enableAdd={amOwner}
            enableDelete={amOwner}
            enableEdit={amOwner}
            emailList={data.report?.emailList || []}
            setEmailList={(updatedList: []) => {
              const newReportValue = data.report ?? { emailList: [] };
              newReportValue.emailList = updatedList;
              setSeasonField("report", newReportValue);
            }}
            subtitle="Add emails you would like reports sent to"
          />

          <div
            className={!amOwner ? "pointer-events-none opacity-50" : undefined}
          >
            <ReportSettingsField
              enableSelectAll={amOwner}
              reportSettings={data.report?.reportSettings}
              season={season}
              setReportSettings={(updatedList: EmailReportSettings) => {
                const newReportValue = data.report ?? { reportSettings: [] };
                newReportValue.reportSettings = updatedList;
                setSeasonField("report", newReportValue);
              }}
              subtitle="Select what information you want included in your report emails"
            />
          </div>
        </div>
      ) : (
        <p className="text-gray-500 text-center mt-10">{season} not enabled</p>
      )}
    </div>
  );
}
type ValidationMessage = {
  fieldName?: string;
  message: string;
};
export type JobValidationStatus = {
  complete: boolean;
  snow: {
    complete: boolean;
  };
  landscape: {
    complete: boolean;
  };
  messages: Array<ValidationMessage>;
};
// TODO: extract this to a shared function in snowLib
export function jobValidationStatus(
  job: Partial<JobInterface>,
): JobValidationStatus {
  const messages: Array<{ fieldName?: string; message: string }> = [];
  const requiredFields: Array<keyof JobInterface> = ["name", "address"];
  for (const field of requiredFields) {
    const value = job[field];
    if (value === undefined || value === null || value === "") {
      messages.push({
        fieldName: field,
        message: `Please fill in ${field}`,
      });
    }
  }
  if (
    typeof job.location?.lat !== "number" ||
    typeof job.location?.lng !== "number"
  ) {
    messages.push({
      message:
        "Invalid address.  Please edit the address and select a match from the drop down.",
      fieldName: "address",
    });
  }
  const coreFieldsComplete = messages.length < 1;

  const noSeason =
    job.snow?.enabled === false && job.landscape?.enabled === false;
  if (noSeason) {
    messages.push({ message: "Please enable at least one season" });
  }

  let snowComplete = true;
  let landscapeComplete = true;
  if (job.snow?.enabled === true) {
    const seasonErrors = validateSeason(Season.snow, job);
    if (seasonErrors.length > 0) {
      messages.push(...seasonErrors);
      snowComplete = false;
    }
  }
  if (job.landscape?.enabled === true) {
    const seasonErrors = validateSeason(Season.landscape, job);
    if (seasonErrors.length > 0) {
      messages.push(...seasonErrors);
      landscapeComplete = false;
    }
  }

  return {
    complete: messages.length === 0 && !noSeason,
    snow: {
      complete: snowComplete && coreFieldsComplete,
    },
    landscape: {
      complete: landscapeComplete && coreFieldsComplete,
    },
    messages: messages,
  };
}
function validateSeason(
  season: Season,
  job: Partial<JobInterface>,
): Array<ValidationMessage> {
  const data: Partial<SeasonDetails> = job[season] ?? {};
  const messages: Array<ValidationMessage> = [];
  if (typeof data.instructions !== "string" || data.instructions.length < 1) {
    messages.push({
      fieldName: season + ".instructions",
      message: `Please fill in crew instructions for ${season}.`,
    });
  }
  if (
    !Array.isArray(data.serviceOptions) ||
    data.serviceOptions.filter((s) => s.checked).length < 1
  ) {
    messages.push({
      fieldName: season + ".serviceOptions",
      message: `Please select at least one ${season} service.`,
    });
  }
  return messages;
}
function ErrorMessage({
  error,
  defaultMessage = "Unknown Error",
}: {
  error: unknown;
  defaultMessage: string;
}) {
  return (
    <div className="w-full py-10 text-xl flex flex-col items-center text-center">
      <h2 className="">Error</h2>
      <div className="text-lg">
        {error != null && typeof error === "object" ? (
          <>
            {isAxiosError(error) ? (
              <>
                {error.response?.data?.message ??
                  error.response?.data ??
                  defaultMessage}
              </>
            ) : "message" in error ? (
              error.message
            ) : (
              defaultMessage
            )}
          </>
        ) : (
          defaultMessage
        )}
      </div>
    </div>
  );
}
function capitalizeFirstLetter(val: string) {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}
function Label({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`text-sm font-bold mb-0.5 self-start text-snow-primary ${className ?? ""}`}
    >
      {children}
    </div>
  );
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function MapThumb({ job }: { job: Partial<JobInterface> }) {
  return (
    <div>
      <div className="border border-2 w-[200px] h-[200px] text-gray-300 flex items-center justify-center">
        <svg
          className="h-[50px] opacity-50"
          width="79.285"
          height="123.4"
          version="1.1"
          viewBox="0 0 20.978 32.65"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="m10.488-0.0039a10.489 10.489 0 0 0-10.489 10.489 10.489 10.489 0 0 0 0.94723 4.332c-0.0036-1e-3 -0.0072-2e-3 -0.01085-3e-3 1.2467 3.0507 5.978 10.669 9.4806 15.209 2.8281-3.7247 6.2697-8.6803 9.2806-14.539a10.489 10.489 0 0 0 0.24236-0.47284c0.03208-0.0634 0.06466-0.12549 0.09664-0.18913-0.0045 1e-3 -0.0089 2e-3 -0.01344 3e-3a10.489 10.489 0 0 0 0.95447-4.3408 10.489 10.489 0 0 0-10.489-10.489zm0 5.4095a5.0791 5.0791 0 0 1 5.0793 5.0793 5.0791 5.0791 0 0 1-5.0793 5.0793 5.0791 5.0791 0 0 1-5.0793-5.0793 5.0791 5.0791 0 0 1 5.0793-5.0793z"
            fill="#d40000"
          />
          <ellipse
            cx="10.488"
            cy="31.786"
            rx="3.6666"
            ry=".86027"
            fillOpacity=".34434"
          />
        </svg>
      </div>
    </div>
  );
}

// function MapThumbWatcher({ job }: { job: Partial<JobData> }) {
//   const map = useMap();
//   // If our location changes, recenter the map on it.
//   useEffect(() => {
//     if (map && job.location) {
//       map.setCenter(job.location);
//     }
//   }, [job.location, map]);
//   return null;
// }

const requiredImageOptions = [
  { label: "Disabled", value: -1 },
  { label: 0, value: 0 },
  { label: 1, value: 1 },
  { label: 2, value: 2 },
  { label: 3, value: 3 },
  { label: 4, value: 4 },
  { label: 5, value: 5 },
  { label: 6, value: 6 },
  { label: 7, value: 7 },
  { label: 8, value: 8 },
  { label: 9, value: 9 },
  { label: 10, value: 10 },
];

export function RequiredImageSelector({
  setSeasonField,
  data,
  disabled = false,
}: {
  setSeasonField: (field: keyof SeasonDetails, value: unknown) => void;
  data: SeasonDetails;
  disabled?: boolean;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <div>
        <div className="text-xs text-snow-primary font-bold mb-0.5">
          After Images
        </div>
        <div className="w-full max-w-sm">
          <Select
            onChange={(value) =>
              setSeasonField("requiredImageCount", value?.value)
            }
            options={requiredImageOptions.filter((o) => o.value !== -1)}
            placeholder="Number of Required Property Images"
            isDisabled={disabled}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderColor: "#082D4A",
              }),
            }}
            value={requiredImageOptions.find(
              (o) => o.value == data.requiredImageCount,
            )}
          />
        </div>
      </div>
      <div>
        <div className="text-xs text-snow-primary font-bold mb-0.5">
          Before Images
        </div>
        <div className="w-full max-w-sm">
          <Select
            onChange={(value) => {
              const v = value?.value;
              setSeasonField("beforeImagesEnabled", v !== -1);
              setSeasonField("beforeImagesRequiredCount", v === -1 ? 0 : v);
            }}
            options={[...requiredImageOptions]}
            placeholder="Number of Required Property Images"
            isDisabled={disabled}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderColor: "#082D4A",
              }),
            }}
            value={
              data.beforeImagesEnabled === true
                ? requiredImageOptions.find(
                    (o) => o.value == data.beforeImagesRequiredCount,
                  )
                : requiredImageOptions[0]
            }
          />
        </div>
      </div>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapStateToProps = (state: any) => {
  const { currentUser } = state;
  return { currentUser };
};

export default connect(mapStateToProps)(EditJobPage);
export { EditJobPage };
